import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useId, useRef, useState } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/services/auth0/session";
import {
  requiresSameWorkReview,
  ShipmentWorkReview,
  type WorkReviewRequest,
} from "./shipment-work-review";

type PhysicalAddress = {
  line1: string;
  line2: string | null;
  city: string;
  province: string;
  country: string;
  postalCode: string | null;
  plusCode?: string | null;
  location?: { lat: number; lng: number } | null;
};
type Effect = {
  id: string;
  kind: string;
  targetId: string;
  state: string;
  labelDisposition: string | null;
  replacementTaskId?: string | null;
  externalLabelOutcome?: string | null;
  replacementLabelId?: string | null;
};
type PickupPackageQuote = {
  currency: "CAD";
  oldNetCents: number;
  oldTaxCents: number;
  newNetCents: number;
  newTaxCents: number;
  deltaCents: number;
};
type OfferFulfillment = {
  inductionKind: string;
  timeZone: string;
  pickupDate: string | null;
  readyAt: string | null;
  handoffBy: string | null;
  dispatchCenter: {
    id: string;
    name: string | null;
    code: string | null;
    displayAvailability: "CAPTURED" | "UNKNOWN_LEGACY";
    address: PhysicalAddress | null;
  } | null;
  carrierDropoff: {
    id: string;
    name: string | null;
    displayAvailability: "CAPTURED" | "UNKNOWN_LEGACY";
    address: PhysicalAddress | null;
  } | null;
};
type Offer = {
  currency: string;
  fulfillment: OfferFulfillment;
  pickupPackageQuote: PickupPackageQuote | null;
  deliveredPrecision: "DATE" | "DATETIME";
  timeZone: string;
  pickupVisit?: {
    oldGrossCents: number;
    newGrossCents: number;
    deltaCents: number;
  } | null;
  id: string;
  serviceName: string;
  serviceCode: string;
  totalCents: number;
  expiresAt: string;
  deliveredFastest: string;
  deliveredSlowest: string;
  readyAt?: string | null;
};
type VisitDraft = {
  address: PhysicalAddress;
  timeZone: string;
  contact: {
    contactName: string;
    contactCompany?: string | null;
    contactPhone?: string | null;
    contactEmail?: string | null;
    instructions?: string | null;
  };
  scheduledDate: string;
  pickupServiceId: string;
  slotId: string;
};
type PackageCandidate = {
  id: string;
  index: number;
  kind: string | null;
  weightGrams: number;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  weightOriginal: string | null;
  weightOriginalUnit: string | null;
  lengthOriginal: string | null;
  widthOriginal: string | null;
  heightOriginal: string | null;
  dimensionOriginalUnit: string | null;
  description: string | null;
  declaredValueCents: number | null;
  isFragile: boolean;
  requiresCooling: boolean;
  packageTypeId: string | null;
};
type Context = {
  packageCandidate: PackageCandidate[] | null;
  pickupBooking: {
    id: string;
    pickupAddress: PhysicalAddress;
    contactName: string | null;
    contactPhone: string | null;
    contactEmail: string | null;
    instructions: string | null;
    scheduledDate: string;
    slotId: string;
    carrierAddOnServiceId: string;
  } | null;
  pickupSlots: Array<{
    id: string;
    name: string;
    startTime: string;
    endTime: string;
    carrierAddOnServiceId: string;
    carrierAddOnService: { name: string };
    dispatchCenter: { name: string };
  }>;
  labelCancellationOperations: Array<{
    labelId: string;
    state: string;
    failureReason: string | null;
    providerReference: string;
  }>;
  supplierAccount: {
    provider: string | null;
    owner: "CUSTOMER" | "EZX";
  } | null;
  parties: Array<{
    type: string;
    address: PhysicalAddress | null;
    name: string | null;
    company: string | null;
    phone: string | null;
    email: string | null;
    instructions: string | null;
  }>;
  pendingCancellation: boolean;
  requestedPickupAddress: PhysicalAddress | null;
  state: string;
  fingerprint: string;
  requestId: string;
  shipmentId: string;
  changes: Array<{
    addressType: string;
    before: PhysicalAddress | null;
    after: PhysicalAddress;
  }>;
  commercialDeltaCents: number | null;
  commercialReason: string | null;
  effects: Effect[];
  offer: {
    pickupVisit: {
      id: string;
      sourceBookingId: string;
      destinationBookingId: string | null;
    } | null;
    pickupDate: string | null;
    handoffBy: string | null;
    dispatch: PhysicalAddress | null;
    dropoff: PhysicalAddress | null;
    currency: string;
    pickupPackageQuote: PickupPackageQuote | null;
    deliveredPrecision: "DATE" | "DATETIME";
    timeZone: string;
    service: { name: string; code: string };
    deliveredFastest: string;
    deliveredSlowest: string;
    expiresAt: string;
    readyAt: string | null;
  } | null;
  funding: { id: string; status: string; amountCents: number } | null;
  refundProgress: {
    remainingCents: number;
    settledCents: number;
    unresolved: boolean;
  } | null;
  refund: { id: string; settlementGroups: Array<{ status: string }> } | null;
  packages: Array<{
    id: string;
    index: number;
    weightGrams: number;
    lengthCm: string | number;
    widthCm: string | number;
    heightCm: string | number;
    packageKind: string;
    declaredValueCents: number | null;
  }>;
  labels: Array<{
    id: string;
    providerRef: string | null;
    trackingNumber: string | null;
    packageId: string | null;
    status: string;
    purchaseOutcome: string | null;
  }>;
  tasks: Array<{
    id: string;
    status: string;
    type: string;
    tripId: string | null;
  }>;
  creditSources: Array<{
    id: string;
    currency: string;
    sourceRef: string | null;
    remainingCents: number;
  }>;
  refundableComponents: Array<{
    shipmentId: string;
    chargeType: "LABEL" | "DELIVERY" | "PICKUP" | "FEE";
    remainingGrossCents: number;
    providerCarrier?: { name: string } | null;
  }>;
};
type ReviewRequest = WorkReviewRequest & {
  requestTypes?: string[];
  id: string;
  shipmentId: string;
  addressLine2Correction?: string | null;
  addressChange?: { state: string } | null;
};
type ExternalDocument = {
  verifiedPartiesAndService: true;
  outcome: "AMENDED" | "REPLACED";
  providerReference: string;
  trackingNumber: string;
  artifactBase64: string;
  evidence: string;
  supplierChargeCents: number;
  supplierCreditCents: number;
  supplierCurrency: string;
  creditSourceEventId?: string;
  supplierCreditReference?: string;
  pieces: Array<{ packageId: string; trackingNumber: string }>;
};
type FailedPurchaseFound = {
  action: "CONFIRM_FAILED_PURCHASE_FOUND";
  labelId: string;
  evidence: string;
  providerReference: string;
  trackingNumber: string;
  artifactBase64: string;
  supplierChargeCents: number;
  supplierCurrency: string;
  pieces: Array<{ packageId: string; trackingNumber: string }>;
};
type Command =
  | FailedPurchaseFound
  | {
      action: "CONFIRM_FAILED_PURCHASE_NO_EFFECT";
      labelId: string;
      evidence: string;
    }
  | { action: "RESUME_AFTER_CANCELLATION_REJECTION" }
  | { action: "CONFIRM_VOID_ONLY_LABEL"; effectId: string; evidence: string }
  | {
      action: "RECONCILE_CANCELLATION_LABEL";
      labelId: string;
      outcome: "VOIDED" | "NOT_VOIDED";
      evidence: string;
    }
  | { action: "RECONCILE_FOR_CANCELLATION" }
  | { action: "BEGIN_EXTERNAL_LABEL"; effectId: string }
  | {
      action:
        | "MARK_EXTERNAL_LABEL_UNKNOWN"
        | "CONFIRM_NO_EXTERNAL_LABEL_CHANGE";
      effectId: string;
      evidence: string;
    }
  | { action: "APPLY_ADDRESS_CHANGE"; sameAcceptedWorkConfirmed?: boolean }
  | { action: "LINK_SETTLED_FUNDING"; paymentId: string }
  | { action: "LINK_SETTLED_REFUND"; refundRequestId: string }
  | {
      action: "CONFIRM_LABEL_DISPOSITION";
      effectId: string;
      disposition: "NOT_PRINTED" | "COPIES_SECURED";
      evidence: string;
    }
  | ({ action: "CONFIRM_EXTERNAL_LABEL"; effectId: string } & ExternalDocument)
  | {
      action: "REQUEST_COMPONENT_REFUND";
      allocations: Array<{
        chargeType: "LABEL" | "DELIVERY" | "PICKUP" | "FEE";
        amountGrossCents: number;
      }>;
    };
type Body =
  | { action: "ADDRESS_APPLICATION_CONTEXT" }
  | {
      action: "PREPARE_ADDRESS_OFFERS";
      readyAt?: string;
      pickupVisit?: VisitDraft;
      addressCandidate?: PhysicalAddress;
      addressCandidates?: Array<{
        addressType: "FROM" | "TO";
        address: PhysicalAddress;
      }>;
    }
  | {
      status: "ACTIONED";
      sameAcceptedWorkConfirmed?: boolean;
      offerId?: string;
      commercialDeltaCents?: number;
      commercialTaxCents?: number;
      pickupDeltaCents?: number;
      pickupTaxDeltaCents?: number;
      commercialReason?: string;
    }
  | { application: Command; fingerprint: string };
const promiseTime = (
  value: string,
  precision: "DATE" | "DATETIME",
  timeZone: string
) =>
  precision === "DATE"
    ? new Date(value).toLocaleDateString(undefined, { timeZone: "UTC" })
    : new Date(value).toLocaleString(undefined, { timeZone });
const dollars = (amount: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(
    amount / 100
  );
const fieldClass =
  "w-full rounded border border-input bg-background p-2 text-sm";
const printAddress = (value: PhysicalAddress | null) =>
  value
    ? [
        value.line1,
        value.line2,
        value.city,
        value.province,
        value.postalCode,
        value.country,
        value.plusCode,
        value.location &&
        Number.isFinite(value.location.lat) &&
        Number.isFinite(value.location.lng)
          ? `(${value.location.lat}, ${value.location.lng})`
          : null,
      ]
        .filter(Boolean)
        .join(", ")
    : "Unavailable";

function PackageDeclaration({ piece }: { piece: PackageCandidate }) {
  const fields = [
    ["id", "Package ID"],
    ["index", "Position"],
    ["kind", "Kind"],
    ["packageTypeId", "Package type ID"],
    ["weightGrams", "Weight (g)"],
    ["lengthCm", "Length (cm)"],
    ["widthCm", "Width (cm)"],
    ["heightCm", "Height (cm)"],
    ["weightOriginal", "Original weight"],
    ["weightOriginalUnit", "Original weight unit"],
    ["lengthOriginal", "Original length"],
    ["widthOriginal", "Original width"],
    ["heightOriginal", "Original height"],
    ["dimensionOriginalUnit", "Original dimension unit"],
    ["description", "Description"],
    ["declaredValueCents", "Declared value (cents)"],
    ["isFragile", "Fragile"],
    ["requiresCooling", "Requires cooling"],
  ] as const;
  return (
    <dl className="grid gap-1 border-t py-2 sm:grid-cols-2">
      {fields.map(([key, label]) => (
        <div key={key}>
          <dt className="font-medium">{label}</dt>
          <dd>
            {piece[key] == null
              ? "Not specified"
              : typeof piece[key] === "boolean"
                ? piece[key]
                  ? "Yes"
                  : "No"
                : String(piece[key])}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function PickupPackagePrice({
  quote,
  accepted,
}: {
  quote: PickupPackageQuote;
  accepted: boolean;
}) {
  return (
    <div className="space-y-1">
      <strong>
        {accepted
          ? "Current reviewed pickup price"
          : "Proposed pickup package price"}{" "}
        ({quote.currency})
      </strong>
      <p>
        Previous: net {dollars(quote.oldNetCents)} + tax{" "}
        {dollars(quote.oldTaxCents)}
      </p>
      <p>
        {accepted ? "Reviewed" : "Proposed"}: net {dollars(quote.newNetCents)} +
        tax {dollars(quote.newTaxCents)}
      </p>
      <p>
        Pickup difference including tax: {dollars(quote.deltaCents)}.{" "}
        {accepted
          ? "Included in the total adjustment, not an additional charge."
          : "Review the pickup portion within the total adjustment below, including any waiver."}
      </p>
    </div>
  );
}
function OfferConditions({ fulfillment }: { fulfillment: OfferFulfillment }) {
  return (
    <div className="space-y-1">
      <strong>Proposed handoff conditions</strong>
      <p>
        {fulfillment.inductionKind} · {fulfillment.timeZone}
      </p>
      <p>
        Pickup date:{" "}
        {fulfillment.pickupDate
          ? promiseTime(fulfillment.pickupDate, "DATE", fulfillment.timeZone)
          : "Not specified"}
      </p>
      <p>
        Ready at:{" "}
        {fulfillment.readyAt
          ? promiseTime(fulfillment.readyAt, "DATETIME", fulfillment.timeZone)
          : "Not specified"}
      </p>
      <p>
        Handoff by:{" "}
        {fulfillment.handoffBy
          ? promiseTime(fulfillment.handoffBy, "DATETIME", fulfillment.timeZone)
          : "Not specified"}
      </p>
      {(
        [
          ["Dispatch center", fulfillment.dispatchCenter],
          ["Carrier dropoff", fulfillment.carrierDropoff],
        ] as const
      ).map(([label, facility]) => (
        <div key={label}>
          <strong>{label}</strong>
          {facility ? (
            <>
              <p>
                {facility.displayAvailability === "UNKNOWN_LEGACY"
                  ? "Name not recorded"
                  : facility.name}
                {"code" in facility && facility.code
                  ? ` (${facility.code})`
                  : ""}
              </p>
              <p>{printAddress(facility.address)}</p>
            </>
          ) : (
            <p>Not applicable</p>
          )}
        </div>
      ))}
    </div>
  );
}

export function AddressChangeReview({
  request,
  endpoint,
  isAdmin,
  onFinished,
}: {
  request: ReviewRequest;
  endpoint: string;
  isAdmin: boolean;
  onFinished: (state: string) => void;
}) {
  const controlPrefix = useId();
  const { token } = useSession();
  const client = useQueryClient();
  const [structured, setStructured] = useState(Boolean(request.addressChange));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [selected, setSelected] = useState("");
  const [readyAt, setReadyAt] = useState("");
  const [visit, setVisit] = useState<VisitDraft | null>(null);
  const [amount, setAmount] = useState("0.00");
  const [pickupAmount, setPickupAmount] = useState("");
  const [pickupTax, setPickupTax] = useState("");
  const [includedTax, setIncludedTax] = useState("0.00");
  const [reason, setReason] = useState("");
  const [confirmedScope, setConfirmedScope] = useState<string | null>(null);
  const [candidate, setCandidate] = useState({
    line1: "",
    line2: "",
    city: "",
    province: "",
    country: "CA",
    postalCode: "",
  });
  const [senderCandidate, setSenderCandidate] = useState({
    line1: "",
    line2: "",
    city: "",
    province: "",
    country: "CA",
    postalCode: "",
  });
  const multipleSides =
    request.requestTypes?.includes("SENDER_ADDRESS_CORRECTION") &&
    request.requestTypes?.includes("RECIPIENT_ADDRESS_CORRECTION");
  const execute = async <T,>(body: Body) =>
    (
      await axios.patch<T>(endpoint, body, {
        headers: { Authorization: `Bearer ${token}` },
      })
    ).data;
  const context = useQuery({
    queryKey: ["address-change-application", request.id],
    queryFn: () => execute<Context>({ action: "ADDRESS_APPLICATION_CONTEXT" }),
    enabled: structured && Boolean(token),
    refetchInterval: 5000,
  });
  const data = context.data;
  const needsWorkReview = requiresSameWorkReview(request);
  const reviewScope = JSON.stringify([
    request.id,
    request.requestTypes,
    request.contactName,
    request.contactPhone,
    request.contactEmail,
    request.deliveryInstructions,
    data?.fingerprint,
    data?.state,
    selected,
    candidate,
    senderCandidate,
    visit,
    readyAt,
    amount,
    includedTax,
    pickupAmount,
    pickupTax,
    reason,
  ]);
  const sameWorkConfirmed = confirmedScope === reviewScope;
  useEffect(() => {
    setConfirmedScope((confirmed) =>
      confirmed === reviewScope ? confirmed : null
    );
  }, [reviewScope]);
  const selectedOffer = offers.find((offer) => offer.id === selected);
  const selectedVisit = selectedOffer?.pickupVisit;
  const needsPickupReview = Boolean(
    selectedVisit || selectedOffer?.pickupPackageQuote
  );
  const pickupAmountsEntered = [pickupAmount, pickupTax].every((value) =>
    /^-?\d+(\.\d{1,2})?$/.test(value.trim())
  );
  const act = async (work: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await work();
      await client.invalidateQueries({
        predicate: (query) =>
          [
            "address-change-application",
            "pendingShipmentChangeRequests",
            "shipment",
            "shipments",
            "shipment-change-requests",
            "shipmentHistory",
            "orders",
            "order",
            "carrier",
          ].includes(String(query.queryKey[0])),
      });
    } catch (caught) {
      if (
        axios.isAxiosError<{ code?: string; error?: string }>(caught) &&
        (caught.response?.data?.code ?? caught.response?.data?.error) ===
          "LABEL_PURCHASE_RECOVERY_REQUIRED"
      )
        setStructured(true);
      setError(
        axios.isAxiosError<{ message?: string; error?: string }>(caught)
          ? (caught.response?.data?.message ??
              caught.response?.data?.error ??
              caught.message)
          : caught instanceof Error
            ? caught.message
            : "The action could not finish"
      );
    } finally {
      setConfirmedScope(null);
      setBusy(false);
    }
  };
  const application = (command: Command | (() => Command)) =>
    act(async () => {
      if (!data) return;
      const resolved = typeof command === "function" ? command() : command;
      if (
        resolved.action === "APPLY_ADDRESS_CHANGE" &&
        needsWorkReview &&
        !sameWorkConfirmed
      )
        throw new Error("Review the contact and instruction impact first");
      const result = await execute<{ state: string; nextRequestId?: string }>({
        application:
          resolved.action === "APPLY_ADDRESS_CHANGE" && needsWorkReview
            ? { ...resolved, sameAcceptedWorkConfirmed: sameWorkConfirmed }
            : resolved,
        fingerprint: data.fingerprint,
      });
      await context.refetch();
      if (
        result.nextRequestId ||
        ["APPLIED", "RECONCILED_FOR_CANCELLATION"].includes(result.state)
      )
        onFinished(result.state);
    });
  const prepare = () =>
    act(async () => {
      const result = await execute<{ offers: Offer[] }>({
        action: "PREPARE_ADDRESS_OFFERS",
        ...(visit ? { pickupVisit: visit } : {}),
        readyAt: readyAt ? new Date(readyAt).toISOString() : undefined,
        ...(!structured && !visit && request.addressLine2Correction == null
          ? multipleSides
            ? {
                addressCandidates: [
                  {
                    addressType: "FROM",
                    address: {
                      ...senderCandidate,
                      location: null,
                      plusCode: null,
                    },
                  },
                  {
                    addressType: "TO",
                    address: { ...candidate, location: null, plusCode: null },
                  },
                ],
              }
            : {
                addressCandidate: {
                  ...candidate,
                  location: null,
                  plusCode: null,
                },
              }
          : {}),
      });
      setStructured(true);
      setOffers(result.offers);
      setSelected("");
      await context.refetch();
      if (!result.offers.length)
        setError(
          "No compatible offer is available. Review the address, service and planned ready time."
        );
    });
  const approve = () =>
    act(async () => {
      if (needsWorkReview && !sameWorkConfirmed)
        throw new Error("Review the contact and instruction impact first");
      if (needsPickupReview && !pickupAmountsEntered)
        throw new Error(
          "Enter the signed pickup amount and tax change; enter 0 in both for a waiver"
        );
      if ((selected || needsPickupReview) && !reason.trim())
        throw new Error("Explain the reviewed price difference or waiver");
      const delta = Math.round(Number(amount) * 100);
      if (!Number.isFinite(delta)) throw new Error("Enter a valid adjustment");
      const taxCents = Math.round(Number(includedTax) * 100);
      if (
        delta > 0 &&
        (!Number.isSafeInteger(taxCents) || taxCents < 0 || taxCents > delta)
      )
        throw new Error("Review the tax included in the additional charge");
      const result = await execute<{
        request?: { status: string };
        data?: { status: string };
      }>({
        status: "ACTIONED",
        ...(needsWorkReview
          ? { sameAcceptedWorkConfirmed: sameWorkConfirmed }
          : {}),
        ...(selected ? { offerId: selected } : {}),
        commercialDeltaCents: delta,
        commercialTaxCents: delta > 0 ? taxCents : undefined,
        ...(needsPickupReview
          ? {
              pickupDeltaCents: Math.round(Number(pickupAmount) * 100),
              pickupTaxDeltaCents: Math.round(Number(pickupTax) * 100),
            }
          : {}),
        commercialReason: reason || undefined,
      });
      setStructured(true);
      if ((result.request ?? result.data)?.status === "ACTIONED")
        onFinished("APPLIED");
      else await context.refetch();
    });
  return (
    <div className="min-w-0 space-y-4 text-sm leading-relaxed [overflow-wrap:anywhere] [&_fieldset]:min-w-0 [&_button]:max-w-full [&_button]:whitespace-normal [&_button]:h-auto [&_button]:min-h-9">
      {data && !context.error && <AddressChangeDecision change={data} />}
      {!data && !context.error && (
        <p className="text-muted-foreground">
          Review the requested change below.
        </p>
      )}
      {data?.state !== "APPLIED" && (
        <ShipmentWorkReview
          request={request}
          checked={sameWorkConfirmed}
          onCheckedChange={(checked) =>
            setConfirmedScope(checked ? reviewScope : null)
          }
          disabled={busy}
        />
      )}
      {error && (
        <p
          role="alert"
          className="rounded border border-red-500 p-3 text-red-600"
        >
          {error}
        </p>
      )}
      {context.error && structured && (
        <p role="alert">
          Could not load the application checklist. Refresh before acting.
        </p>
      )}
      {data?.packageCandidate && (
        <section className="space-y-2 rounded border p-3">
          <h3 className="font-semibold">Package declaration review</h3>
          <h4>Current active packages</h4>
          {data.packages.map((piece) => (
            <p key={piece.id}>
              {piece.id}: {piece.weightGrams} g · {piece.lengthCm} ×{" "}
              {piece.widthCm} × {piece.heightCm} cm
            </p>
          ))}
          <h4>Complete proposed package list</h4>
          <p>
            This list replaces the active package list. Current package IDs
            absent below are removed; new IDs are additions.
          </p>
          {data.packageCandidate.map((piece) => (
            <PackageDeclaration key={piece.id} piece={piece} />
          ))}
        </section>
      )}
      {data?.pendingCancellation && (
        <div className="rounded border p-3 space-y-2">
          <p>
            Cancellation review is pending. Record all started payment/carrier
            outcomes first; keep the original work held. No new carrier purchase
            or replacement task will be started here.
          </p>
          <Button
            variant="outline"
            disabled={busy}
            onClick={() =>
              application({ action: "RECONCILE_FOR_CANCELLATION" })
            }
          >
            Reconcile correction and hand over to normal cancellation review
          </Button>
        </div>
      )}
      {data?.state === "RECONCILED_FOR_CANCELLATION" &&
        !data.pendingCancellation && (
          <div className="rounded border p-3">
            <p>
              The cancellation was rejected. Work remains held because the prior
              correction has recorded financial/carrier outcomes.
            </p>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() =>
                application({ action: "RESUME_AFTER_CANCELLATION_REJECTION" })
              }
            >
              Continue as a fresh correction review with customer consent
            </Button>
          </div>
        )}
      {isAdmin &&
        data?.labelCancellationOperations
          .filter((operation) =>
            ["PROCESSING", "UNKNOWN"].includes(operation.state)
          )
          .map((operation) => (
            <CancellationLabelRecovery
              key={operation.labelId}
              operation={operation}
              disabled={busy}
              submit={(outcome, evidence) =>
                application({
                  action: "RECONCILE_CANCELLATION_LABEL",
                  labelId: operation.labelId,
                  outcome,
                  evidence,
                })
              }
            />
          ))}
      {isAdmin &&
        data?.labels
          .filter(
            (label) => label.status === "FAILED" && !label.purchaseOutcome
          )
          .map((label) => (
            <FailedPurchaseRecovery
              key={label.id}
              labelId={label.id}
              packages={data.packages.filter(
                (pkg) => !label.packageId || pkg.id === label.packageId
              )}
              disabled={busy}
              submit={(command) => application(command)}
            />
          ))}
      {!structured && request.addressLine2Correction == null && (
        <fieldset className="grid gap-2 sm:grid-cols-2">
          <legend>Prepare the complete address from this request</legend>
          {(Object.keys(candidate) as Array<keyof typeof candidate>).map(
            (field) => (
              <label key={field} htmlFor={`${controlPrefix}-1-${field}`}>
                {
                  {
                    line1: "Street address",
                    line2: "Unit",
                    city: "City",
                    province: "Province / state",
                    country: "Country",
                    postalCode: "Postal code",
                  }[field]
                }
                <Input
                  id={`${controlPrefix}-1-${field}`}
                  className={fieldClass}
                  value={candidate[field]}
                  onChange={(event) =>
                    setCandidate((old) => ({
                      ...old,
                      [field]: event.target.value,
                    }))
                  }
                />
              </label>
            )
          )}
        </fieldset>
      )}
      {!structured &&
        multipleSides &&
        request.addressLine2Correction == null && (
          <fieldset className="grid gap-2 sm:grid-cols-2">
            <legend>
              Complete sender candidate for the original multi-side request
            </legend>
            {(
              Object.keys(senderCandidate) as Array<
                keyof typeof senderCandidate
              >
            ).map((field) => (
              <label key={field} htmlFor={`${controlPrefix}-2-${field}`}>
                {
                  {
                    line1: "Sender street",
                    line2: "Sender Unit",
                    city: "Sender city",
                    province: "Sender province / state",
                    country: "Sender country",
                    postalCode: "Sender postal code",
                  }[field]
                }
                <Input
                  id={`${controlPrefix}-2-${field}`}
                  className={fieldClass}
                  value={senderCandidate[field]}
                  onChange={(event) =>
                    setSenderCandidate((previous) => ({
                      ...previous,
                      [field]: event.target.value,
                    }))
                  }
                />
              </label>
            ))}
          </fieldset>
        )}
      {(!data ||
        data.state === "WAITING_REVIEW" ||
        ["WAITING_CUSTOMER", "WAITING_FUNDING", "PREPARING"].includes(
          data.state
        )) && (
        <details
          open={!data || data.state === "WAITING_REVIEW"}
          className="rounded-lg border p-4"
        >
          <summary className="cursor-pointer font-medium">
            Service and price
          </summary>
          <div className="mt-3 space-y-3">
            <label className="block" htmlFor={`${controlPrefix}-3`}>
              Planned ready time for replacement work (your browser timezone:{" "}
              {Intl.DateTimeFormat().resolvedOptions().timeZone})
              <Input
                id={`${controlPrefix}-3`}
                className={fieldClass}
                type="datetime-local"
                value={readyAt}
                onChange={(event) => setReadyAt(event.target.value)}
              />
            </label>
            <p className="text-xs text-muted-foreground">
              Replanning delivery does not move an existing pickup. The
              dispatcher assigns replacement delivery work.
            </p>
            {data?.pickupBooking && (
              <PickupVisitEditor
                context={data}
                value={visit}
                onChange={setVisit}
                disabled={busy}
              />
            )}
            <Button variant="outline" disabled={busy} onClick={prepare}>
              {data?.state === "WAITING_CUSTOMER"
                ? "Refresh the unaccepted offer"
                : "Get revised service offers"}
            </Button>
            {offers.length > 0 && (
              <label className="block" htmlFor={`${controlPrefix}-4`}>
                Service and delivery promise
                <Select
                  value={selected}
                  onValueChange={(value) => {
                    setSelected(value);
                    setPickupAmount("");
                    setPickupTax("");
                  }}
                >
                  <SelectTrigger
                    className="h-auto min-h-9 w-full min-w-0 [&_[data-slot=select-value]]:line-clamp-none [&_[data-slot=select-value]]:block [&_[data-slot=select-value]]:whitespace-normal [&_[data-slot=select-value]]:text-left"
                    id={`${controlPrefix}-4`}
                  >
                    <SelectValue placeholder="Select an offer" />
                  </SelectTrigger>
                  <SelectContent
                    position="popper"
                    className="max-w-[calc(100vw-2rem)] [&_[data-slot=select-item]]:whitespace-normal [&_[data-slot=select-item]]:[overflow-wrap:anywhere]"
                  >
                    {offers.map((offer) => (
                      <SelectItem key={offer.id} value={offer.id}>
                        {offer.serviceName} ({offer.serviceCode}) —{" "}
                        {dollars(offer.totalCents)} —{" "}
                        {promiseTime(
                          offer.deliveredFastest,
                          offer.deliveredPrecision,
                          offer.timeZone
                        )}{" "}
                        to{" "}
                        {promiseTime(
                          offer.deliveredSlowest,
                          offer.deliveredPrecision,
                          offer.timeZone
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            )}
            {selectedOffer && (
              <div className="rounded border p-3 space-y-2">
                <p>Proposed service currency: {selectedOffer.currency}</p>
                <OfferConditions fulfillment={selectedOffer.fulfillment} />
                {selectedOffer.pickupPackageQuote && (
                  <PickupPackagePrice
                    quote={selectedOffer.pickupPackageQuote}
                    accepted={false}
                  />
                )}
              </div>
            )}
            {selectedVisit && (
              <p>
                Pickup quote: {dollars(selectedVisit.oldGrossCents)} →{" "}
                {dollars(selectedVisit.newGrossCents)}. Quoted difference:{" "}
                {dollars(selectedVisit.deltaCents)}. Review the charged amount
                or waiver below.
              </p>
            )}
            <label className="block" htmlFor={`${controlPrefix}-5`}>
              Customer adjustment in CAD (+ charge / − refund)
              <Input
                id={`${controlPrefix}-5`}
                className={fieldClass}
                type="number"
                step="0.01"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </label>
            {Number(amount) > 0 && (
              <label className="block" htmlFor={`${controlPrefix}-6`}>
                Tax included in this additional amount (CAD)
                <Input
                  id={`${controlPrefix}-6`}
                  className={fieldClass}
                  type="number"
                  step="0.01"
                  value={includedTax}
                  onChange={(event) => setIncludedTax(event.target.value)}
                />
              </label>
            )}
            {needsPickupReview && (
              <>
                <label className="block" htmlFor={`${controlPrefix}-7`}>
                  Pickup portion of the total adjustment in CAD (+ charge / −
                  refund / 0 waiver)
                  <Input
                    id={`${controlPrefix}-7`}
                    className={fieldClass}
                    type="number"
                    step="0.01"
                    value={pickupAmount}
                    onChange={(event) => setPickupAmount(event.target.value)}
                  />
                </label>
                <label className="block" htmlFor={`${controlPrefix}-8`}>
                  Change to the pickup tax in CAD (signed)
                  <Input
                    id={`${controlPrefix}-8`}
                    className={fieldClass}
                    type="number"
                    step="0.01"
                    value={pickupTax}
                    onChange={(event) => setPickupTax(event.target.value)}
                  />
                </label>
                <p>
                  The total includes the pickup portion and any separately
                  reviewed shipping/service change. The pickup quote and amount
                  charged to the customer are recorded separately.
                </p>
              </>
            )}
            <label className="block" htmlFor={`${controlPrefix}-9`}>
              Explain the reviewed price difference or waiver
              <Textarea
                id={`${controlPrefix}-9`}
                className={fieldClass}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
            <Button
              variant="default"
              disabled={
                busy ||
                (needsWorkReview && !sameWorkConfirmed) ||
                (offers.length > 0 && !selected) ||
                (selected !== "" && !reason.trim()) ||
                (needsPickupReview && !pickupAmountsEntered)
              }
              onClick={approve}
            >
              Approve proposal
            </Button>
            {data?.state === "WAITING_CUSTOMER" && (
              <p>
                The customer must accept the selected service, dates and
                adjustment. Approval has not applied the address.
              </p>
            )}
          </div>
        </details>
      )}
      {data?.offer && (
        <details className="rounded-lg border p-3">
          <summary className="cursor-pointer font-medium">
            Reviewed service
          </summary>
          <div className="mt-3 space-y-2">
            <strong>{data.offer.service.name}</strong>
            {data.offer.pickupPackageQuote && (
              <PickupPackagePrice
                quote={data.offer.pickupPackageQuote}
                accepted
              />
            )}
            <p>
              {promiseTime(
                data.offer.deliveredFastest,
                data.offer.deliveredPrecision,
                data.offer.timeZone
              )}{" "}
              to{" "}
              {promiseTime(
                data.offer.deliveredSlowest,
                data.offer.deliveredPrecision,
                data.offer.timeZone
              )}
            </p>
            {data.offer.pickupDate && (
              <p>
                Reviewed pickup date:{" "}
                {promiseTime(
                  data.offer.pickupDate,
                  "DATE",
                  data.offer.timeZone
                )}
              </p>
            )}
            {data.offer.handoffBy && (
              <p>
                Reviewed handoff by:{" "}
                {promiseTime(
                  data.offer.handoffBy,
                  "DATETIME",
                  data.offer.timeZone
                )}{" "}
                · {data.offer.timeZone}
              </p>
            )}
            {data.offer.dispatch && (
              <p>
                Reviewed dispatch address: {printAddress(data.offer.dispatch)}
              </p>
            )}
            {data.offer.dropoff && (
              <p>
                Reviewed dropoff address: {printAddress(data.offer.dropoff)}
              </p>
            )}
            <p>
              Offer expires {new Date(data.offer.expiresAt).toLocaleString()}
            </p>
            {data.offer.readyAt && (
              <p>
                Planned ready time:{" "}
                {promiseTime(
                  data.offer.readyAt,
                  "DATETIME",
                  data.offer.timeZone
                )}{" "}
                · {data.offer.timeZone}
              </p>
            )}
          </div>
        </details>
      )}
      {data &&
        ["WAITING_FUNDING", "PREPARING", "RECOVERY_REQUIRED"].includes(
          data.state
        ) && (
          <>
            {(data.commercialDeltaCents ?? 0) !== 0 && (
              <div className="rounded border p-3 space-y-2">
                <strong>
                  Settlement: {dollars(data.commercialDeltaCents ?? 0)}
                </strong>
                {data.funding ? (
                  <>
                    <p>Additional payment: {data.funding.status}</p>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        disabled={busy}
                        onClick={() =>
                          data.funding &&
                          application({
                            action: "LINK_SETTLED_FUNDING",
                            paymentId: data.funding.id,
                          })
                        }
                      >
                        Confirm this settled payment
                      </Button>
                    )}
                  </>
                ) : (data.commercialDeltaCents ?? 0) > 0 ? (
                  <p>
                    The customer pays the additional balance using the Order
                    payment options. This checklist updates when its receipt is
                    reserved.
                  </p>
                ) : null}
                {(data.commercialDeltaCents ?? 0) < 0 &&
                  (!data.refund ||
                    (data.refundProgress &&
                      !data.refundProgress.unresolved &&
                      data.refundProgress.remainingCents > 0)) &&
                  isAdmin && (
                    <RefundAllocationForm
                      context={data}
                      disabled={busy}
                      submit={(allocations) =>
                        application({
                          action: "REQUEST_COMPONENT_REFUND",
                          allocations,
                        })
                      }
                    />
                  )}
                {data.refund && (
                  <>
                    <p>
                      Refund request {data.refund.id}:{" "}
                      {data.refund.settlementGroups
                        .map((group) => group.status)
                        .join(", ") || "awaiting normal refund review"}
                    </p>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        disabled={busy}
                        onClick={() =>
                          data.refund &&
                          application({
                            action: "LINK_SETTLED_REFUND",
                            refundRequestId: data.refund.id,
                          })
                        }
                      >
                        Confirm this completed refund
                      </Button>
                    )}
                  </>
                )}
                {!isAdmin && (
                  <p>
                    An administrator completes the financial receipt/refund
                    steps in the same request checklist.
                  </p>
                )}
              </div>
            )}
            {data.effects
              .filter((effect) => effect.kind === "ACTIVE_WORK_FAILURE")
              .map((effect) => (
                <p key={effect.id} className="rounded border p-3">
                  {effect.state === "CONFIRMED"
                    ? "The driver has recorded the failed delivery attempt."
                    : "The assigned driver must record the incorrect-address failure in PAL before delivery can be replanned."}
                </p>
              ))}
            {data.effects
              .filter((effect) =>
                ["LABEL_REPLACEMENT", "LABEL_PURCHASE"].includes(effect.kind)
              )
              .map((effect) => (
                <div key={effect.id} className="rounded border p-3 space-y-2">
                  <strong>
                    {effect.kind === "LABEL_PURCHASE"
                      ? "Prepare shipping label"
                      : "Replace shipping label"}
                    {data.labels.find((label) => label.id === effect.targetId)
                      ?.trackingNumber && (
                      <span className="ml-2 font-normal text-muted-foreground">
                        {
                          data.labels.find(
                            (label) => label.id === effect.targetId
                          )?.trackingNumber
                        }
                      </span>
                    )}
                  </strong>
                  <p>
                    Printed copies:{" "}
                    {effect.labelDisposition === "NOT_PRINTED"
                      ? "Never printed"
                      : (effect.labelDisposition ?? "not yet secured")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      disabled={
                        busy ||
                        effect.kind === "LABEL_PURCHASE" ||
                        Boolean(effect.labelDisposition)
                      }
                      onClick={() =>
                        application({
                          action: "CONFIRM_LABEL_DISPOSITION",
                          effectId: effect.id,
                          disposition: "NOT_PRINTED",
                          evidence:
                            "Reviewer confirms this label was never printed",
                        })
                      }
                    >
                      Label was never printed
                    </Button>
                    <Button
                      variant="outline"
                      disabled={
                        busy ||
                        effect.kind === "LABEL_PURCHASE" ||
                        Boolean(effect.labelDisposition)
                      }
                      onClick={() =>
                        application({
                          action: "CONFIRM_LABEL_DISPOSITION",
                          effectId: effect.id,
                          disposition: "COPIES_SECURED",
                          evidence:
                            "Reviewer confirms all old printed copies are secured and will not be used",
                        })
                      }
                    >
                      Confirm all old printed copies are secured
                    </Button>
                  </div>
                  {isAdmin &&
                    (effect.kind === "LABEL_PURCHASE" ||
                      data.labels.find((label) => label.id === effect.targetId)
                        ?.providerRef) &&
                    effect.state === "REQUIRED" && (
                      <Button
                        variant="outline"
                        disabled={busy || data.pendingCancellation}
                        onClick={() =>
                          application({
                            action: "BEGIN_EXTERNAL_LABEL",
                            effectId: effect.id,
                          })
                        }
                      >
                        Reserve carrier operation after readiness checks
                      </Button>
                    )}
                  {isAdmin &&
                    ["PROCESSING", "UNKNOWN"].includes(effect.state) && (
                      <ExternalRecovery
                        disabled={busy}
                        submit={(action, evidence) =>
                          application({ action, effectId: effect.id, evidence })
                        }
                      />
                    )}
                  {effect.kind === "LABEL_PURCHASE" ||
                  data.labels.find((label) => label.id === effect.targetId)
                    ?.providerRef ? (
                    effect.state === "REQUIRED" ? (
                      <p>
                        Reserve this operation before making any changes in the
                        carrier account.
                      </p>
                    ) : effect.state === "CONFIRMED" ? (
                      <p>
                        Carrier document outcome recorded. Do not repeat the
                        carrier action.
                      </p>
                    ) : isAdmin ? (
                      <ExternalDocumentForm
                        key={effect.id}
                        context={data}
                        effect={effect}
                        disabled={busy}
                        submit={(document) =>
                          application({
                            action: "CONFIRM_EXTERNAL_LABEL",
                            effectId: effect.id,
                            ...document,
                          })
                        }
                      />
                    ) : (
                      <p>
                        An administrator records the exact carrier outcome and
                        replacement document here.
                      </p>
                    )
                  ) : (
                    <p>
                      The internal PDF will be regenerated from the applied
                      complete address.
                    </p>
                  )}
                </div>
              ))}
            <Button
              variant="default"
              disabled={
                busy ||
                (needsWorkReview && !sameWorkConfirmed) ||
                data.pendingCancellation ||
                data.effects.some(
                  (effect) =>
                    effect.kind === "ACTIVE_WORK_FAILURE" &&
                    effect.state !== "CONFIRMED"
                )
              }
              onClick={() => application({ action: "APPLY_ADDRESS_CHANGE" })}
            >
              Apply change
            </Button>
          </>
        )}
      {data && !context.error && (
        <details className="rounded-lg border p-3">
          <summary className="cursor-pointer font-medium">Details</summary>
          <div className="mt-3 space-y-4">
            {data.changes.length > 0 && (
              <section aria-label="Recorded addresses" className="space-y-3">
                <h3 className="font-semibold">Recorded addresses</h3>
                {data.changes.map((side) => (
                  <div key={side.addressType}>
                    <h4 className="font-medium">
                      {side.addressType === "FROM" ? "Sender" : "Recipient"}
                    </h4>
                    <p>Current: {printAddress(side.before)}</p>
                    <p>Requested: {printAddress(side.after)}</p>
                  </div>
                ))}
              </section>
            )}
            {data.commercialReason && (
              <p>Price adjustment reason: {data.commercialReason}</p>
            )}
            <AddressChangeConsequences
              change={data}
              shipmentId={data.shipmentId}
            />
            <section
              aria-label="Current work and settlement"
              className="rounded border p-3 space-y-2"
            >
              <h3 className="font-semibold">Current work and settlement</h3>
              <p>
                Current Tasks below are live work, not a record of which Tasks
                were preserved by this correction.
              </p>
              {data.tasks.map((task) => (
                <p key={task.id}>
                  {task.type.split("_").join(" ")} · {task.id}: {task.status} ·{" "}
                  {task.tripId ? `Trip ${task.tripId}` : "No Trip assigned"}
                </p>
              ))}
              {!data.tasks.length && <p>No current Tasks returned.</p>}
              <p>
                Current pickup Booking:{" "}
                {data.pickupBooking ? data.pickupBooking.id : "None"}. This is
                not historical adoption evidence.
              </p>
              <p>
                Funding receipt:{" "}
                {data.funding
                  ? `${data.funding.status} · ${dollars(data.funding.amountCents)}`
                  : "Not recorded"}
              </p>
              <p>
                Refund:{" "}
                {data.refund
                  ? data.refund.settlementGroups
                      .map((group) => group.status)
                      .join(", ") || "Awaiting normal refund review"
                  : "Not recorded"}
              </p>
              {data.refundProgress && (
                <p>
                  Refund settled: {dollars(data.refundProgress.settledCents)} ·
                  Remaining: {dollars(data.refundProgress.remainingCents)} ·{" "}
                  {data.refundProgress.unresolved
                    ? "Outcome unresolved; review required"
                    : "No unresolved outcome reported"}
                </p>
              )}
            </section>
          </div>
        </details>
      )}
    </div>
  );
}

const reviewStages: Record<string, { title: string; next?: string }> = {
  WAITING_REVIEW: {
    title: "Review change",
    next: "Review service and price, then approve or decline.",
  },
  WAITING_CUSTOMER: {
    title: "Waiting for customer",
    next: "The customer must accept the revised terms.",
  },
  WAITING_FUNDING: {
    title: "Waiting for settlement",
    next: "Complete the required payment or refund below.",
  },
  PREPARING: {
    title: "Prepare change",
    next: "Complete the required steps below, then apply the change.",
  },
  RECOVERY_REQUIRED: {
    title: "Check incomplete outcome",
    next: "Resolve the outstanding payment or label outcome before continuing.",
  },
  APPLIED: { title: "Change applied" },
  REJECTED: { title: "Request declined" },
  RECONCILED_FOR_CANCELLATION: { title: "With cancellation review" },
};

function ReviewAddress({
  value,
  previous,
}: {
  value: PhysicalAddress | null;
  previous?: PhysicalAddress | null;
}) {
  if (!value)
    return <span className="text-muted-foreground">Not recorded</span>;
  const fields = [
    "line1",
    "line2",
    "city",
    "province",
    "postalCode",
    "country",
  ] as const;
  return (
    <p className="leading-relaxed">
      {fields
        .filter((key) => value[key] || previous?.[key])
        .map((key, index) => (
          <span key={key}>
            {index > 0 && ", "}
            <span
              className={
                previous && value[key] !== previous[key]
                  ? "rounded bg-primary/10 px-0.5 font-medium"
                  : undefined
              }
            >
              {value[key] ||
                (key === "line2"
                  ? "Unit removed"
                  : key === "postalCode"
                    ? "Postal code removed"
                    : "Removed")}
            </span>
          </span>
        ))}
    </p>
  );
}

function AddressChangeDecision({ change }: { change: Context }) {
  const stage = reviewStages[change.state] ?? {
    title: "Review status unavailable",
  };
  const impacts = [
    change.effects.some((effect) =>
      ["LABEL_REPLACEMENT", "LABEL_PURCHASE"].includes(effect.kind)
    ) && "New label",
    change.effects.some((effect) =>
      ["TASK_REPLAN", "ACTIVE_WORK_FAILURE"].includes(effect.kind)
    ) && "Delivery replanning",
    change.effects.some((effect) => effect.kind === "PICKUP_VISIT_CHANGE") &&
      "Pickup change",
  ].filter(Boolean);
  return (
    <section aria-label="Change summary" className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">{stage.title}</h3>
          {(change.pendingCancellation || stage.next) && (
            <p className="mt-1 text-muted-foreground">
              {change.pendingCancellation
                ? "Cancellation review is pending. Reconcile this correction below."
                : stage.next}
            </p>
          )}
        </div>
        <p className="font-medium tabular-nums">
          {change.commercialDeltaCents == null
            ? "Price not reviewed"
            : change.commercialDeltaCents === 0
              ? "No price adjustment"
              : `${change.commercialDeltaCents > 0 ? "Charge" : "Refund"}: ${dollars(Math.abs(change.commercialDeltaCents))} CAD`}
        </p>
      </div>
      {change.changes.map((side) => (
        <div key={side.addressType} className="rounded-lg border p-4">
          <h4 className="mb-3 font-medium">
            {side.addressType === "FROM"
              ? "Sender address"
              : "Recipient address"}
          </h4>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Current</p>
              <ReviewAddress value={side.before} />
            </div>
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Requested</p>
              <ReviewAddress value={side.after} previous={side.before} />
            </div>
          </div>
        </div>
      ))}
      {impacts.length > 0 && (
        <p className="text-xs text-muted-foreground">{impacts.join(" · ")}</p>
      )}
    </section>
  );
}

function RefundAllocationForm({
  context,
  disabled,
  submit,
}: {
  context: Context;
  disabled: boolean;
  submit: (
    allocations: Extract<
      Command,
      { action: "REQUEST_COMPONENT_REFUND" }
    >["allocations"]
  ) => void;
}) {
  const controlPrefix = useId();
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  return (
    <fieldset className="space-y-2">
      <legend>
        Select original paid components for the remaining accepted refund
      </legend>
      <p>
        Remaining accepted refund:{" "}
        {dollars(
          context.refundProgress?.remainingCents ??
            -(context.commercialDeltaCents ?? 0)
        )}
        . The server validates the exact accepted refund and preserves each
        component's original funding and provider.
      </p>
      {context.refundableComponents.map((component) => (
        <label
          key={component.chargeType}
          className="block"
          htmlFor={`${controlPrefix}-10-${component.chargeType}`}
        >
          {component.chargeType} — {component.providerCarrier?.name ?? "EZX"} —{" "}
          {dollars(component.remainingGrossCents)} remaining
          <Input
            id={`${controlPrefix}-10-${component.chargeType}`}
            type="number"
            min="0"
            step="0.01"
            value={amounts[component.chargeType] ?? "0.00"}
            disabled={disabled}
            onChange={(event) =>
              setAmounts((previous) => ({
                ...previous,
                [component.chargeType]: event.target.value,
              }))
            }
          />
        </label>
      ))}
      <Button
        variant="outline"
        disabled={disabled}
        onClick={() =>
          submit(
            context.refundableComponents
              .map((component) => ({
                chargeType: component.chargeType,
                amountGrossCents: Math.round(
                  Number(amounts[component.chargeType] ?? "0") * 100
                ),
              }))
              .filter((allocation) => allocation.amountGrossCents > 0)
          )
        }
      >
        Create refund request from selected components
      </Button>
    </fieldset>
  );
}

function ExternalDocumentForm({
  context,
  effect,
  disabled,
  submit,
}: {
  context: Context;
  effect: Effect;
  disabled: boolean;
  submit: (document: ExternalDocument) => void;
}) {
  const controlPrefix = useId();
  const label = context.labels.find((item) => item.id === effect.targetId);
  const [outcome, setOutcome] = useState<"AMENDED" | "REPLACED">(
    effect.kind === "LABEL_PURCHASE" ? "REPLACED" : "AMENDED"
  );
  const [reference, setReference] = useState(label?.providerRef ?? "");
  const [tracking, setTracking] = useState(label?.trackingNumber ?? "");
  const [verified, setVerified] = useState(false);
  const { pdf, fileError, selectPdfFile } = useCarrierPdfUpload();
  const [evidence, setEvidence] = useState("");
  const [charge, setCharge] = useState("0");
  const [credit, setCredit] = useState("0");
  const [currency, setCurrency] = useState("CAD");
  const [creditSource, setCreditSource] = useState("");
  const [creditReference, setCreditReference] = useState("");
  const [pieces, setPieces] = useState<Record<string, string>>({});
  const packages = context.packages.filter(
    (pkg) => !label?.packageId || label.packageId === pkg.id
  );
  return (
    <fieldset className="space-y-2">
      <legend>Record the carrier's actual outcome</legend>
      {context.supplierAccount && (
        <p>
          Supplier: {context.supplierAccount.provider} —{" "}
          {context.supplierAccount.owner === "EZX"
            ? "EZX account"
            : "This customer's account"}
        </p>
      )}
      {context.parties.map((party) => (
        <div key={party.type}>
          <strong>
            {party.type === "FROM" ? "Label sender" : "Recipient"}
          </strong>
          <p>{printAddress(party.address)}</p>
          <p>
            {party.name} {party.company} · {party.phone} · {party.email}
          </p>
          <p>{party.instructions}</p>
        </div>
      ))}
      {context.packages.map((pkg) => (
        <p key={pkg.id}>
          Package {pkg.index + 1}: {pkg.weightGrams} g · {pkg.lengthCm} ×{" "}
          {pkg.widthCm} × {pkg.heightCm} cm · {pkg.packageKind}
          {pkg.declaredValueCents !== null
            ? ` · declared value ${dollars(pkg.declaredValueCents)}`
            : ""}
        </p>
      ))}
      <p>
        In the booked carrier account, amend or replace the label for the exact
        reviewed address/service. Keep the outcome reference and upload the
        resulting PDF. If the outcome is uncertain, do not buy again or mark
        this step confirmed.
      </p>
      <label className="block" htmlFor={`${controlPrefix}-11`}>
        Confirmed outcome
        <Select
          value={outcome}
          onValueChange={(value) =>
            setOutcome(value === "REPLACED" ? "REPLACED" : "AMENDED")
          }
        >
          <SelectTrigger
            className="h-auto min-h-9 w-full min-w-0 [&_[data-slot=select-value]]:line-clamp-none [&_[data-slot=select-value]]:block [&_[data-slot=select-value]]:whitespace-normal [&_[data-slot=select-value]]:text-left"
            id={`${controlPrefix}-11`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent
            position="popper"
            className="max-w-[calc(100vw-2rem)] [&_[data-slot=select-item]]:whitespace-normal [&_[data-slot=select-item]]:[overflow-wrap:anywhere]"
          >
            <SelectItem value="AMENDED">
              Carrier amended the same reservation
            </SelectItem>
            <SelectItem value="REPLACED">
              Carrier confirmed a replacement
            </SelectItem>
          </SelectContent>
        </Select>
      </label>
      <label className="block" htmlFor={`${controlPrefix}-12`}>
        Carrier reservation / replacement reference
        <Input
          id={`${controlPrefix}-12`}
          className={fieldClass}
          value={reference}
          onChange={(event) => setReference(event.target.value)}
        />
      </label>
      <label className="block" htmlFor={`${controlPrefix}-13`}>
        Master carrier tracking number
        <Input
          id={`${controlPrefix}-13`}
          className={fieldClass}
          value={tracking}
          onChange={(event) => setTracking(event.target.value)}
        />
      </label>
      {outcome === "REPLACED" &&
        packages.map((pkg) => (
          <label
            className="block"
            key={pkg.id}
            htmlFor={`${controlPrefix}-14-${pkg.id}`}
          >
            Package {pkg.index + 1} carrier tracking
            <Input
              id={`${controlPrefix}-14-${pkg.id}`}
              className={fieldClass}
              value={pieces[pkg.id] ?? ""}
              onChange={(event) =>
                setPieces((old) => ({ ...old, [pkg.id]: event.target.value }))
              }
            />
          </label>
        ))}
      <label className="block" htmlFor={`${controlPrefix}-15`}>
        Actual carrier PDF
        <Input
          id={`${controlPrefix}-15`}
          className={fieldClass}
          type="file"
          accept="application/pdf"
          onChange={(event) => selectPdfFile(event.target.files?.[0])}
        />
      </label>
      {fileError && <p role="alert">{fileError}</p>}
      <label className="block" htmlFor={`${controlPrefix}-16`}>
        Carrier confirmation details
        <Textarea
          id={`${controlPrefix}-16`}
          className={fieldClass}
          value={evidence}
          onChange={(event) => setEvidence(event.target.value)}
        />
      </label>
      <label className="block" htmlFor={`${controlPrefix}-17`}>
        Actual new supplier charge (native currency)
        <Input
          id={`${controlPrefix}-17`}
          className={fieldClass}
          type="number"
          step="0.01"
          value={charge}
          onChange={(event) => setCharge(event.target.value)}
        />
      </label>
      <label className="block" htmlFor={`${controlPrefix}-18`}>
        New charge currency
        <Input
          id={`${controlPrefix}-18`}
          className={fieldClass}
          value={currency}
          onChange={(event) => setCurrency(event.target.value.toUpperCase())}
        />
      </label>
      <label className="block" htmlFor={`${controlPrefix}-19`}>
        Original supplier charge being credited
        <Select
          value={creditSource || "NONE"}
          onValueChange={(value) =>
            setCreditSource(value === "NONE" ? "" : value)
          }
        >
          <SelectTrigger
            className="h-auto min-h-9 w-full min-w-0 [&_[data-slot=select-value]]:line-clamp-none [&_[data-slot=select-value]]:block [&_[data-slot=select-value]]:whitespace-normal [&_[data-slot=select-value]]:text-left"
            id={`${controlPrefix}-19`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent
            position="popper"
            className="max-w-[calc(100vw-2rem)] [&_[data-slot=select-item]]:whitespace-normal [&_[data-slot=select-item]]:[overflow-wrap:anywhere]"
          >
            <SelectItem value="NONE">No supplier credit</SelectItem>
            {context.creditSources.map((source) => (
              <SelectItem key={source.id} value={source.id}>
                {source.sourceRef} — {source.currency}{" "}
                {(source.remainingCents / 100).toFixed(2)} remaining
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      {creditSource && (
        <>
          <label className="block" htmlFor={`${controlPrefix}-20`}>
            Actual credit in the original charge currency
            <Input
              id={`${controlPrefix}-20`}
              className={fieldClass}
              type="number"
              step="0.01"
              value={credit}
              onChange={(event) => setCredit(event.target.value)}
            />
          </label>
          <label className="block" htmlFor={`${controlPrefix}-21`}>
            Supplier credit receipt reference
            <Input
              id={`${controlPrefix}-21`}
              className={fieldClass}
              value={creditReference}
              onChange={(event) => setCreditReference(event.target.value)}
            />
          </label>
        </>
      )}
      <label className="flex gap-2" htmlFor={`${controlPrefix}-22`}>
        <Checkbox
          id={`${controlPrefix}-22`}
          checked={verified}
          onCheckedChange={(checked) => setVerified(checked === true)}
        />
        I checked the actual carrier PDF/reservation against the approved
        addresses, contacts, service and packages above
      </label>
      <Button
        variant="outline"
        disabled={
          disabled ||
          !verified ||
          !pdf ||
          evidence.trim().length < 10 ||
          !reference ||
          !tracking
        }
        onClick={() =>
          submit({
            verifiedPartiesAndService: true,
            outcome,
            providerReference: reference,
            trackingNumber: tracking,
            artifactBase64: pdf,
            evidence,
            supplierChargeCents: Math.round(Number(charge) * 100),
            supplierCreditCents: creditSource
              ? Math.round(Number(credit) * 100)
              : 0,
            supplierCurrency: currency,
            creditSourceEventId: creditSource || undefined,
            supplierCreditReference: creditReference || undefined,
            pieces:
              outcome === "REPLACED"
                ? packages.map((pkg) => ({
                    packageId: pkg.id,
                    trackingNumber: pieces[pkg.id] ?? "",
                  }))
                : [],
          })
        }
      >
        Record confirmed carrier outcome
      </Button>
    </fieldset>
  );
}

function ExternalRecovery({
  disabled,
  submit,
}: {
  disabled: boolean;
  submit: (
    action:
      | "MARK_EXTERNAL_LABEL_UNKNOWN"
      | "CONFIRM_NO_EXTERNAL_LABEL_CHANGE"
      | "CONFIRM_VOID_ONLY_LABEL",
    evidence: string
  ) => void;
}) {
  const controlPrefix = useId();
  const [evidence, setEvidence] = useState("");
  return (
    <div className="space-y-2">
      <p>
        Use the recorded operation reference to check the carrier account. Do
        not repeat a purchase while the result is uncertain.
      </p>
      <label htmlFor={`${controlPrefix}-23`}>
        Reconciliation evidence
        <Textarea
          id={`${controlPrefix}-23`}
          className={fieldClass}
          value={evidence}
          onChange={(event) => setEvidence(event.target.value)}
        />
      </label>
      <Button
        variant="outline"
        disabled={disabled || evidence.trim().length < 10}
        onClick={() => submit("MARK_EXTERNAL_LABEL_UNKNOWN", evidence)}
      >
        Record uncertain outcome and hold work
      </Button>
      <Button
        variant="outline"
        disabled={disabled || evidence.trim().length < 10}
        onClick={() => submit("CONFIRM_NO_EXTERNAL_LABEL_CHANGE", evidence)}
      >
        Carrier confirms no change or charge occurred
      </Button>
      <Button
        variant="outline"
        disabled={disabled || evidence.trim().length < 10}
        onClick={() => submit("CONFIRM_VOID_ONLY_LABEL", evidence)}
      >
        Original reservation was voided; no replacement was purchased
      </Button>
    </div>
  );
}

function PickupVisitEditor({
  context,
  value,
  onChange,
  disabled,
}: {
  context: Context;
  value: VisitDraft | null;
  onChange: (value: VisitDraft | null) => void;
  disabled: boolean;
}) {
  const controlPrefix = useId();
  const source = context.pickupBooking;
  if (!source) return null;
  const requested = context.requestedPickupAddress ?? source.pickupAddress;
  return (
    <fieldset className="space-y-2 rounded border p-3">
      <legend>Booked collection visit — separate from label sender</legend>
      <p>Current visit: {printAddress(source.pickupAddress)}</p>
      {context.requestedPickupAddress && (
        <p>
          Requested visit address:{" "}
          {printAddress(context.requestedPickupAddress)}
        </p>
      )}
      <label htmlFor={`${controlPrefix}-24`}>
        <Checkbox
          id={`${controlPrefix}-24`}
          checked={Boolean(value)}
          disabled={disabled}
          onCheckedChange={(checked) =>
            onChange(
              checked === true
                ? {
                    address: {
                      line1: requested.line1,
                      line2: requested.line2,
                      city: requested.city,
                      province: requested.province,
                      country: requested.country,
                      postalCode: requested.postalCode,
                      location: null,
                      plusCode: null,
                    },
                    timeZone: "",
                    contact: {
                      contactName: source.contactName ?? "",
                      contactPhone: source.contactPhone,
                      contactEmail: source.contactEmail,
                      instructions: source.instructions,
                    },
                    scheduledDate: source.scheduledDate.slice(0, 10),
                    pickupServiceId: source.carrierAddOnServiceId,
                    slotId: source.slotId,
                  }
                : null
            )
          }
        />{" "}
        Explicitly move this Shipment's actual collection visit; leave other
        parcels on their original visit
      </label>
      {value && (
        <>
          <div className="grid gap-2 sm:grid-cols-2">
            {(
              [
                "line1",
                "line2",
                "city",
                "province",
                "country",
                "postalCode",
              ] as const
            ).map((key) => (
              <label key={key} htmlFor={`${controlPrefix}-25-${key}`}>
                {
                  {
                    line1: "Street",
                    line2: "Unit",
                    city: "City",
                    province: "Province / state",
                    country: "Country",
                    postalCode: "Postal code",
                  }[key]
                }
                <Input
                  id={`${controlPrefix}-25-${key}`}
                  className={fieldClass}
                  value={value.address[key] ?? ""}
                  disabled={disabled}
                  onChange={(event) =>
                    onChange({
                      ...value,
                      address: {
                        ...value.address,
                        [key]: event.target.value,
                        location: null,
                        plusCode: null,
                      },
                    })
                  }
                />
              </label>
            ))}
          </div>
          <label className="block" htmlFor={`${controlPrefix}-26`}>
            Visit timezone (for example America/Toronto)
            <Input
              id={`${controlPrefix}-26`}
              className={fieldClass}
              value={value.timeZone}
              disabled={disabled}
              onChange={(event) =>
                onChange({ ...value, timeZone: event.target.value })
              }
            />
          </label>
          <label className="block" htmlFor={`${controlPrefix}-27`}>
            Collection date
            <Input
              id={`${controlPrefix}-27`}
              className={fieldClass}
              type="date"
              value={value.scheduledDate}
              disabled={disabled}
              onChange={(event) =>
                onChange({ ...value, scheduledDate: event.target.value })
              }
            />
          </label>
          <label className="block" htmlFor={`${controlPrefix}-28`}>
            Pickup service and window
            <Select
              value={value.slotId}
              disabled={disabled}
              onValueChange={(slotId) => {
                const slot = context.pickupSlots.find(
                  (option) => option.id === slotId
                );
                if (slot)
                  onChange({
                    ...value,
                    slotId: slot.id,
                    pickupServiceId: slot.carrierAddOnServiceId,
                  });
              }}
            >
              <SelectTrigger
                className="h-auto min-h-9 w-full min-w-0 [&_[data-slot=select-value]]:line-clamp-none [&_[data-slot=select-value]]:block [&_[data-slot=select-value]]:whitespace-normal [&_[data-slot=select-value]]:text-left"
                id={`${controlPrefix}-28`}
              >
                <SelectValue placeholder="Choose a pickup service and window" />
              </SelectTrigger>
              <SelectContent
                position="popper"
                className="max-w-[calc(100vw-2rem)] [&_[data-slot=select-item]]:whitespace-normal [&_[data-slot=select-item]]:[overflow-wrap:anywhere]"
              >
                {context.pickupSlots.map((slot) => (
                  <SelectItem key={slot.id} value={slot.id}>
                    {slot.carrierAddOnService.name} — {slot.dispatchCenter.name}{" "}
                    — {slot.name} {slot.startTime}–{slot.endTime}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          {(
            [
              "contactName",
              "contactPhone",
              "contactEmail",
              "instructions",
            ] as const
          ).map((key) => (
            <label
              className="block"
              key={key}
              htmlFor={`${controlPrefix}-29-${key}`}
            >
              {
                {
                  contactName: "Visit contact name",
                  contactPhone: "Contact phone",
                  contactEmail: "Contact email",
                  instructions: "Pickup instructions",
                }[key]
              }
              <Input
                id={`${controlPrefix}-29-${key}`}
                className={fieldClass}
                value={value.contact[key] ?? ""}
                disabled={disabled}
                onChange={(event) =>
                  onChange({
                    ...value,
                    contact: { ...value.contact, [key]: event.target.value },
                  })
                }
              />
            </label>
          ))}
          <p>
            The offered date/window, contact, price and downstream delivery
            promise require customer acceptance. Running work must fail normally
            before its replacement is created.
          </p>
        </>
      )}
    </fieldset>
  );
}

function CancellationLabelRecovery({
  operation,
  disabled,
  submit,
}: {
  operation: Context["labelCancellationOperations"][number];
  disabled: boolean;
  submit: (outcome: "VOIDED" | "NOT_VOIDED", evidence: string) => void;
}) {
  const [evidence, setEvidence] = useState("");
  return (
    <fieldset className="rounded border p-3 space-y-2">
      <legend>Normal cancellation needs carrier reconciliation</legend>
      <p>
        Reservation: {operation.providerReference} — {operation.state}. Check
        the booked carrier account; do not repeat a void/refund while its
        outcome is uncertain.
      </p>
      <Textarea
        value={evidence}
        onChange={(event) => setEvidence(event.target.value)}
        aria-label="Observed carrier cancellation result"
      />
      <Button
        variant="outline"
        disabled={disabled || evidence.trim().length < 10}
        onClick={() => submit("VOIDED", evidence)}
      >
        Carrier confirms reservation was voided
      </Button>
      <Button
        variant="outline"
        disabled={disabled || evidence.trim().length < 10}
        onClick={() => submit("NOT_VOIDED", evidence)}
      >
        Carrier confirms no void/refund occurred
      </Button>
    </fieldset>
  );
}

function FailedPurchaseRecovery({
  labelId,
  packages,
  disabled,
  submit,
}: {
  labelId: string;
  packages: Array<{ id: string; index: number }>;
  disabled: boolean;
  submit: (
    command:
      | FailedPurchaseFound
      | {
          action: "CONFIRM_FAILED_PURCHASE_NO_EFFECT";
          labelId: string;
          evidence: string;
        }
  ) => void;
}) {
  const controlPrefix = useId();
  const [evidence, setEvidence] = useState("");
  const [reference, setReference] = useState("");
  const [tracking, setTracking] = useState("");
  const [charge, setCharge] = useState("0");
  const [currency, setCurrency] = useState("CAD");
  const [pieces, setPieces] = useState<Record<string, string>>({});
  const { pdf, fileError, selectPdfFile } = useCarrierPdfUpload();
  return (
    <fieldset className="space-y-2 rounded border p-3">
      <legend>Resolve original failed purchase {labelId}</legend>
      <p>
        Check the original carrier account before another purchase. Record only
        the observed original outcome; this does not buy a label.
      </p>
      <label className="block" htmlFor={`${controlPrefix}-30`}>
        Carrier account evidence
        <Textarea
          id={`${controlPrefix}-30`}
          value={evidence}
          onChange={(event) => setEvidence(event.target.value)}
        />
      </label>
      <Button
        variant="outline"
        disabled={disabled || evidence.trim().length < 10}
        onClick={() =>
          submit({
            action: "CONFIRM_FAILED_PURCHASE_NO_EFFECT",
            labelId,
            evidence,
          })
        }
      >
        Carrier confirms no reservation or charge exists
      </Button>
      <label className="block" htmlFor={`${controlPrefix}-31`}>
        Found original reservation reference
        <Input
          id={`${controlPrefix}-31`}
          value={reference}
          onChange={(event) => setReference(event.target.value)}
        />
      </label>
      <label className="block" htmlFor={`${controlPrefix}-32`}>
        Original tracking number
        <Input
          id={`${controlPrefix}-32`}
          value={tracking}
          onChange={(event) => setTracking(event.target.value)}
        />
      </label>
      <label className="block" htmlFor={`${controlPrefix}-33`}>
        Actual original supplier charge
        <Input
          id={`${controlPrefix}-33`}
          type="number"
          min="0"
          step="0.01"
          value={charge}
          onChange={(event) => setCharge(event.target.value)}
        />
      </label>
      <label className="block" htmlFor={`${controlPrefix}-34`}>
        Original charge currency
        <Input
          id={`${controlPrefix}-34`}
          value={currency}
          onChange={(event) => setCurrency(event.target.value.toUpperCase())}
        />
      </label>
      <label className="block" htmlFor={`${controlPrefix}-35`}>
        Actual original carrier PDF
        <Input
          id={`${controlPrefix}-35`}
          type="file"
          accept="application/pdf"
          onChange={(event) => selectPdfFile(event.target.files?.[0])}
        />
      </label>
      {packages.map((pkg) => (
        <label key={pkg.id} htmlFor={`${controlPrefix}-36-${pkg.id}`}>
          Original package {pkg.index + 1} tracking
          <Input
            id={`${controlPrefix}-36-${pkg.id}`}
            value={pieces[pkg.id] ?? ""}
            onChange={(event) =>
              setPieces((old) => ({ ...old, [pkg.id]: event.target.value }))
            }
          />
        </label>
      ))}
      {fileError && <p role="alert">{fileError}</p>}
      <Button
        variant="outline"
        disabled={
          disabled ||
          evidence.trim().length < 10 ||
          !reference.trim() ||
          !tracking.trim() ||
          !pdf
        }
        onClick={() =>
          submit({
            action: "CONFIRM_FAILED_PURCHASE_FOUND",
            labelId,
            evidence,
            providerReference: reference,
            trackingNumber: tracking,
            artifactBase64: pdf,
            supplierChargeCents: Math.round(Number(charge) * 100),
            supplierCurrency: currency,
            pieces: packages.map((pkg) => ({
              packageId: pkg.id,
              trackingNumber: pieces[pkg.id] ?? "",
            })),
          })
        }
      >
        Record the existing original reservation
      </Button>
    </fieldset>
  );
}

function useCarrierPdfUpload() {
  const [pdf, setPdf] = useState("");
  const [fileError, setFileError] = useState<string | null>(null);
  const selectedFile = useRef<File | null>(null);
  const selectPdfFile = (file: File | undefined) => {
    selectedFile.current = file ?? null;
    setPdf("");
    setFileError(null);
    if (!file || file.size > 10 * 1024 * 1024) {
      if (file) setFileError("Choose a PDF smaller than 10 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (selectedFile.current === file)
        setPdf(
          typeof reader.result === "string"
            ? (reader.result.split(",")[1] ?? "")
            : ""
        );
    };
    reader.onerror = () => {
      if (selectedFile.current === file)
        setFileError("The file could not be read");
    };
    reader.readAsDataURL(file);
  };
  return { pdf, fileError, selectPdfFile };
}

/** Outcomes come from the correction receipt, never from revision numbers. */
export function AddressChangeConsequences({
  change,
  shipmentId,
}: {
  change: {
    state: string;
    commercialDeltaCents?: number | null;
    effects?: Effect[];
    offer?: {
      pickupVisit?: {
        id: string;
        sourceBookingId: string;
        destinationBookingId: string | null;
      } | null;
    } | null;
  };
  shipmentId: string;
}) {
  const taskHref = (id: string) =>
    `/trips/tasks?${new URLSearchParams({ shipmentId, taskId: id })}`;
  return (
    <section
      aria-label="Correction consequences"
      className="rounded border p-3 space-y-2"
    >
      <h3 className="font-semibold">Correction consequences</h3>
      <p>
        {change.state === "APPLIED"
          ? "Applied correction. Task execution and payment outcomes remain separate."
          : change.state === "REJECTED"
            ? "Rejected correction. This proposal was not applied."
            : change.state === "RECONCILED_FOR_CANCELLATION"
              ? "Reconciled for cancellation. This is not an applied correction."
              : "Pending correction. Current work is not proof that these changes were applied."}
      </p>
      <p>
        Reviewed price adjustment:{" "}
        {change.commercialDeltaCents == null
          ? "Not recorded"
          : dollars(change.commercialDeltaCents)}
      </p>
      {change.offer?.pickupVisit && (
        <div className="space-y-1">
          <p>
            <a
              className="underline"
              href={`/pickup-bookings?${new URLSearchParams({ bookingId: change.offer.pickupVisit.sourceBookingId })}`}
            >
              View source Booking {change.offer.pickupVisit.sourceBookingId}
            </a>
          </p>
          {change.offer.pickupVisit.destinationBookingId ? (
            <p>
              <a
                className="underline"
                href={`/pickup-bookings?${new URLSearchParams({ bookingId: change.offer.pickupVisit.destinationBookingId })}`}
              >
                View recorded destination Booking{" "}
                {change.offer.pickupVisit.destinationBookingId}
              </a>
            </p>
          ) : (
            <p>Destination Booking not recorded.</p>
          )}
          <p>
            A recorded destination identifies the prepared visit; it does not
            prove adoption or completed pickup.
          </p>
        </div>
      )}
      {!change.effects?.length && <p>No consequence receipt recorded.</p>}
      {change.effects?.map((effect) => (
        <div key={effect.id} className="space-y-1 border-t pt-2">
          <p>
            {effect.kind.split("_").join(" ")}:{" "}
            {effect.state.split("_").join(" ")}
          </p>
          {["TASK_REPLAN", "ACTIVE_WORK_FAILURE"].includes(effect.kind) ? (
            <>
              <a className="underline" href={taskHref(effect.targetId)}>
                View retained Task {effect.targetId}
              </a>
              {effect.replacementTaskId ? (
                <p>
                  <a
                    className="underline"
                    href={taskHref(effect.replacementTaskId)}
                  >
                    View replacement Task {effect.replacementTaskId}
                  </a>
                </p>
              ) : effect.kind === "TASK_REPLAN" ? (
                <p>Replacement Task not recorded.</p>
              ) : null}
              {effect.kind === "TASK_REPLAN" && (
                <p>
                  Replacement work requires dispatcher feasibility review and
                  assignment. A receipt does not prove its current assignment or
                  completion.
                </p>
              )}
            </>
          ) : effect.kind === "PICKUP_VISIT_CHANGE" ? (
            <p>
              Pickup visit change{" "}
              {effect.state === "CONFIRMED" ? "confirmed" : "pending"}.
              {!change.offer?.pickupVisit &&
                " Historical source and destination Booking identities are not supplied by this receipt."}
            </p>
          ) : (
            <p>
              Target: {effect.targetId}
              {effect.externalLabelOutcome
                ? ` · Carrier outcome: ${effect.externalLabelOutcome}`
                : ""}
              {effect.replacementLabelId
                ? ` · Recorded replacement label: ${effect.replacementLabelId}`
                : ""}
              {effect.labelDisposition
                ? ` · Printed label copies: ${effect.labelDisposition === "NOT_PRINTED" ? "Never printed" : effect.labelDisposition}`
                : ""}
            </p>
          )}
        </div>
      ))}
    </section>
  );
}
